import {FilenameUtil, UIUtil, Util} from 'yougile-util'
import {ExtFile} from '../app/types/extfile'
import {Size} from '../app/types/geometry'
import {Backend} from './backend'
import {UtilUi} from './utils-ui'

const previewSize = 256

export class FilePreview {
  // `<` and `>` are not in pattern to prevent HTML tags in message text from polluting image link
  static rxImg = new RegExp(
    '(/root/#file:(/user-data/[-0-9a-f]+/' +
      `([^ '"\\n<>]+\\.(${UIUtil.imageTypes().join('|')})[^ \n\t<>]*)))`,
    'gi'
  )

  static encodeRelativeUrl(relativeUrl: string): string {
    const path = relativeUrl.split('/')
    const name = path.pop()
    return '/root/#file:' + path.join('/') + '/' + encodeURIComponent(name)
  }

  static encodedToFilenames(html: string): string {
    if (!html) {
      return html
    }
    const parsed = html.replace(FilenameUtil.rx, FilenameUtil.getFilename)
    return parsed.replace(FilenameUtil.rx, item => {
      const link = FilenameUtil.absoluteUrlFromEncoded(
        Util.safeDecodeUriComponent(item, item)
      )
      return Util.safeDecodeUriComponent(
        ' ' + link.split('/').pop(),
        ' ' + link.split('/').pop()
      )
    })
  }

  private static replaceImagesWithHtmlForView(html: string): string {
    return FilePreview.replaceImagesWithHtml(
      html,
      (original, preview, previewSize) => {
        const imgEl = document.createElement('img')
        if (previewSize[0] > 0 && previewSize[1] > 0) {
          if (previewSize[0] > previewSize[1]) {
            imgEl.style.width = '256px'
          } else {
            imgEl.style.height = '256px'
          }
        }
        imgEl.className = 'rounded-4 max-h-320'
        imgEl.src = Backend.toDataUrl(preview)

        const aEl = document.createElement('a')
        aEl.appendChild(imgEl)
        aEl.target = '_blank'
        aEl.className = 'inline-block min-w-56 min-h-56 text-center'
        aEl.tabIndex = -1
        aEl.href = Backend.toDataUrl(original)

        const btn = document.createElement('div')
        btn.className =
          'message-image-btn z-10 absolute right-16 top-16 w-24 h-24 overflow-visible'
        btn.dataset.url = original

        const container = document.createElement('div')
        container.className =
          'message-image-cnt relative inline-block my-16 first:mt-0 last:mb-0'
        container.appendChild(aEl)
        container.appendChild(btn)

        const blockContainer = document.createElement('div')
        blockContainer.className = 'w-full reset mb-8'
        blockContainer.appendChild(container)

        const wrapper = document.createElement('div')
        wrapper.appendChild(blockContainer)
        return wrapper.innerHTML
      }
    )
  }

  private static replaceImagesWithHtmlForEdit(html: string): string {
    return FilePreview.replaceImagesWithHtml(
      html,
      (original, preview, previewSize) => {
        // custom tag is parsed by CKEdtitor
        const el = document.createElement('message-image')
        el.dataset.original = original
        el.dataset.preview = preview
        el.dataset.previewWidth = previewSize[0] + ''
        el.dataset.previewHeight = previewSize[1] + ''

        const wrapper = document.createElement('div')
        wrapper.appendChild(el)
        return wrapper.innerHTML
      }
    )
  }

  private static replaceImagesWithHtml(
    html: string,
    replacer: (
      original: string,
      preview: string,
      previewSize: [number, number]
    ) => string
  ): string {
    return html.replace(FilePreview.rxImg, item => {
      const link = FilenameUtil.relativeUrlFromEncoded(
        Util.safeDecodeUriComponent(item, item)
      )
      const original = FilenameUtil.originalFileFromUrl(link)
      const preview = FilePreview.previewFromUrl(link)

      let width = 0
      let height = 0
      if (preview.includes('-preview@')) {
        const size = preview.split('-preview@').pop()?.split('.')[0]
        if (size) {
          const [w, h] = size.split('x')
          if (+w > 0 && +h > 0) {
            width = +w
            height = +h
          }
        }
      }

      return replacer(original, preview, [width, height])
    })
  }

  private static removeDocumentsFromText(html: string): string {
    return html.replaceAll(
      new RegExp('(\n+)?' + FilenameUtil.rx.source, 'g'),
      ''
    )
  }

  public static replaceEncodedWithHtmlForView(html: string): string {
    html = FilePreview.replaceImagesWithHtmlForView(html)
    html = FilePreview.removeDocumentsFromText(html)
    return html.trim()
  }

  public static replaceEncodedWithHtmlForEdit(html: string): string {
    html = FilePreview.replaceImagesWithHtmlForEdit(html)
    html = FilePreview.removeDocumentsFromText(html)
    return html.trim()
  }

  static getBackgroundPreviewUrl(background: string | undefined): string {
    if (background?.startsWith('/user-data')) {
      return Backend.toDataUrl(FilePreview.previewFromUrl(background)) ?? ''
    } else if (
      background?.startsWith('-images/') &&
      background?.split('/').length === 2
    ) {
      return (
        '/img/backgrounds/thumbs/' + background.split('-images/')[1] + '.jpg'
      )
    } else {
      return ''
    }
  }

  static previewFromUrl(urlWithParameters: string | undefined): string {
    if (!urlWithParameters) {
      return urlWithParameters ?? ''
    }

    // deprecated, old format
    if (urlWithParameters.includes('_____previews')) {
      return (
        urlWithParameters.split('_____previews').shift() + '-256-preview.png'
      )
    }
    if (urlWithParameters.includes('previews[]=-256')) {
      const suffix =
        '-256' +
        urlWithParameters.split('previews[]=-256').pop().split('&').shift()
      return (
        FilenameUtil.originalFileFromUrl(urlWithParameters) + suffix + '.png'
      )
    } else {
      return urlWithParameters
    }
  }

  static getFilePreview(
    source: {file: ExtFile; size?: Size},
    callback: (urlParameters: string, res: ExtFile[]) => void,
    previewMaxSize?: Size,
    options?: {mimeType: string; quality: number; raw: boolean}
  ): void {
    const nameLower = source.file.name.toLowerCase()
    const extensions = UIUtil.imageTypes().map(t => `.${t}`)
    if (extensions.some(e => nameLower.endsWith(e))) {
      FilePreview.getImagePreview(source, callback, previewMaxSize, options)
    } else {
      callback('', [])
    }
  }

  private static fileFromImgSize(size: Size, max: Size): Size {
    const w = size.width
    const h = size.height
    const factor = Math.min(max.height / h, max.width / w)
    return {
      width: Math.round(w * factor),
      height: Math.round(h * factor)
    }
  }

  public static fileFromImgName(name: string, size: Size, max: Size): string {
    return `${name}-${max.height}-preview@${size.width}x${size.height}.png`
  }

  public static fileFromImgSuffix(size: Size, max: Size): string {
    return `-${max.height}-preview@${size.width}x${size.height}`
  }

  /**
   * In Safari, if you try to do `const file = new ExtFile(...)`
   * and then add it to FormData this file will be converted to string
   * “[object ExtFile]”.
   */
  private static fixSafariFormDataBug(file: ExtFile, name: string): void {
    ;(file as {name: string}).name = name
  }

  private static fileFromImg(
    source: {image: HTMLImageElement},
    max: Size,
    name: string,
    callback: (f: ExtFile, ps: string) => void,
    options?: {mimeType: string; quality: number; raw: boolean}
  ): void {
    const {image: img} = source as {image: HTMLImageElement}
    const size = FilePreview.fileFromImgSize(img, max)
    const steps = Math.floor(Math.log(img.width / size.width) / Math.log(2))

    let res: HTMLCanvasElement
    if (steps > 0) {
      for (let i = 0; i < steps; i++) {
        const w = Math.round((res ? res.width : img.width) / 2)
        const h = Math.round((res ? res.height : img.height) / 2)
        const canvasTmp = document.createElement('canvas')
        canvasTmp.width = w
        canvasTmp.height = h
        const ctxTmp = canvasTmp.getContext('2d')
        if (ctxTmp.imageSmoothingQuality) {
          ctxTmp.imageSmoothingQuality = `high`
        }
        ctxTmp.drawImage(res || img, 0, 0, w, h)
        res = canvasTmp
        size.width = w
        size.height = h
      }
    } else {
      const canvas = document.createElement('canvas')
      canvas.width = size.width
      canvas.height = size.height
      const ctx = canvas.getContext('2d')
      if (ctx.imageSmoothingQuality) {
        ctx.imageSmoothingQuality = `high`
      }
      ctx.drawImage(img, 0, 0, size.width, size.height)
      res = canvas
    }
    const {mimeType = 'image/png', quality = 1, raw = true} = options || {}
    UtilUi.canvasToBlob(
      res,
      mimeType,
      quality,
      blob => {
        const file = blob as ExtFile
        FilePreview.fixSafariFormDataBug(
          file,
          FilePreview.fileFromImgName(name, size, max)
        )
        const previewSuffix = FilePreview.fileFromImgSuffix(size, max)
        callback(file, previewSuffix)
      },
      raw
    )
  }

  public static generatePreviews(
    source: {file: ExtFile; image: HTMLImageElement; bytesSize: number},
    callback: (urlParameters: string, res: ExtFile[]) => void,
    previewMaxSize: Size = {height: previewSize, width: previewSize},
    options?: {mimeType: string; quality: number; raw: boolean}
  ): void {
    // if (source.bytesSize < 30 * 1024) {
    //   callback('', [source.file])
    //   return
    // }

    const res: ExtFile[] = []
    const prevSuffixes: string[] = []
    FilePreview.fileFromImg(
      source,
      previewMaxSize,
      source.file.name,
      (f, previewSuffix) => {
        res.push(f)
        prevSuffixes.push(previewSuffix)
        const par = '?previews[]=' + prevSuffixes.join('&previews[]=')
        callback(par, res)
      },
      options
    )
  }

  private static getImagePreview(
    {file}: {file: ExtFile; size?: Size},
    callback: (urlParameters: string, res: ExtFile[]) => void,
    previewMaxSize?: Size,
    options?: {mimeType: string; quality: number; raw: boolean}
  ): void {
    const reader = new FileReader()
    reader.readAsDataURL(file as File)
    reader.onload = () => {
      const url = reader.result as string
      const image = new Image()
      image.onload = () =>
        FilePreview.generatePreviews(
          {file, image, bytesSize: url.length},
          callback,
          previewMaxSize,
          options
        )
      image.src = url
    }
  }

  static previewSize(name: string): {width: number; height: number} {
    const [, size, width = size, height = size] = name.match(
      /(?:-(\d+)-preview)|preview@(\d+)x(\d+).(?:png|jpg|jpeg|gif)$/
    )
    return {width: +width, height: +height}
  }
}
